import { useState } from 'react'
import { activeTodos, allTags, folderCount, trashCount, useStore } from '../store'
import type { Scope } from '../types'
import { signOut } from '../firebase'
import { ContextMenu, type MenuState, menuPos } from './ContextMenu'
import { isNoteDrag, readDragIds } from '../lib/dnd'
import { Settings } from './Settings'
import {
  ChecklistIcon, FolderIcon, NotesIcon, PlusIcon, SidebarIcon, TagIcon, TrashIcon,
} from './icons'

function sameScope(a: Scope, b: Scope): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'folder') return a.id === (b as { id: string }).id
  if (a.kind === 'tag') return a.tag === (b as { tag: string }).tag
  return true
}

export function Sidebar({ onToggle, onPick }: { onToggle: () => void; onPick: () => void }) {
  const store = useStore()
  const { folders, scope, setScope } = store
  const tags = allTags(store)
  const [drafting, setDrafting] = useState(false)
  const [draft, setDraft] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [settings, setSettings] = useState(false)

  /** Folders highlighted for a bulk action, and where a Shift-range measures from. */
  const [picked, setPicked] = useState<string[]>([])
  const [anchor, setAnchor] = useState<string | null>(null)

  // Navigating anywhere else is leaving the selection behind.
  const pick = (s: Scope) => { setPicked([]); setAnchor(null); setScope(s); onPick() }

  /**
   * Shift extends from the anchor, Cmd/Ctrl adds or removes one, and a plain
   * click opens the folder — the same three gestures as the note list.
   * Extending does not navigate: you are choosing what to act on, not where
   * to go.
   */
  const clickFolder = (id: string, e: React.MouseEvent) => {
    if (e.shiftKey) {
      e.preventDefault()
      const from = anchor ?? (scope.kind === 'folder' ? scope.id : null)
      const a = folders.findIndex((f) => f.id === from)
      const b = folders.findIndex((f) => f.id === id)
      setPicked(a < 0 || b < 0
        ? [id]
        : folders.slice(Math.min(a, b), Math.max(a, b) + 1).map((f) => f.id))
      return
    }
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault()
      setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
      setAnchor(id)
      return
    }
    setPicked([id])
    setAnchor(id)
    setScope({ kind: 'folder', id })
    onPick()
  }

  /** The row a note is currently being dragged over, so it can light up. */
  const [dropOn, setDropOn] = useState<string | null>(null)

  /**
   * Drop targets for a note drag. The payload itself cannot be read until the
   * drop — the browser withholds it during dragover — so the type list decides
   * whether to accept.
   */
  const dropTarget = (key: string, apply: (ids: string[]) => void) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!isNoteDrag(e.dataTransfer)) return
      e.preventDefault()                        // without this the drop never fires
      e.dataTransfer.dropEffect = 'move'
      setDropOn(key)
    },
    onDragLeave: () => setDropOn((d) => (d === key ? null : d)),
    onDrop: (e: React.DragEvent) => {
      setDropOn(null)
      const ids = readDragIds(e.dataTransfer)
      if (!ids.length) return
      e.preventDefault()
      apply(ids)
    },
  })

  /** The folders an action applies to: the selection, else the one clicked. */
  const target = (id: string) => (picked.length > 1 && picked.includes(id) ? picked : [id])

  const removeFolders = async (ids: string[]) => {
    const names = folders.filter((f) => ids.includes(f.id)).map((f) => f.name)
    const ok = await store.ask({
      title: ids.length > 1
        ? `Delete these ${ids.length} folders?`
        : `Delete the folder “${names[0] ?? ''}”?`,
      message: 'Their notes move to Recently Deleted.',
      confirmLabel: 'Delete', danger: true,
    })
    if (!ok) return
    store.deleteFolders(ids)
    setPicked([]); setAnchor(null)
  }

  const commitDraft = () => {
    if (draft.trim()) store.addFolder(draft)
    setDraft(''); setDrafting(false)
  }

  return (
    <aside className="pane-sidebar" onMouseDown={() => document.body.setAttribute('data-focus-pane', 'sidebar')}>
      <div className="toolbar">
        <button className="tb-btn" data-role="collapse" onClick={onToggle} title="Hide Sidebar">
          <SidebarIcon />
        </button>
      </div>

      <div className="sidebar-scroll">
        {/* Pinned above everything, the way Quick Notes sits in Apple Notes. */}
        <button className={`folder-row pinned-row ${sameScope(scope, { kind: 'todo' }) ? 'selected' : ''}`}
                onClick={() => pick({ kind: 'todo' })}>
          <span className="icon"><ChecklistIcon /></span>
          <span className="name">To Do</span>
          <span className="count">{activeTodos(store).length || ''}</span>
        </button>

        <div className="sidebar-heading">Indigo Notes</div>

        <button
          className={[
            'folder-row',
            sameScope(scope, { kind: 'all' }) ? 'selected' : '',
            dropOn === 'all' ? 'drop-on' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => pick({ kind: 'all' })}
          {...dropTarget('all', (ids) => store.moveMany(ids, null))}
        >
          <span className="icon"><NotesIcon /></span>
          <span className="name">All Notes</span>
          <span className="count">{folderCount(store, null)}</span>
        </button>

        {folders.map((f) => (
          <div
            key={f.id}
            tabIndex={0}
            role="button"
            className={[
              'folder-row',
              sameScope(scope, { kind: 'folder', id: f.id }) ? 'selected' : '',
              picked.includes(f.id) ? 'picked' : '',
              dropOn === f.id ? 'drop-on' : '',
            ].filter(Boolean).join(' ')}
            {...dropTarget(f.id, (ids) => store.moveMany(ids, f.id))}
            onClick={(e) => { if (renaming !== f.id) clickFolder(f.id, e) }}
            onDoubleClick={() => setRenaming(f.id)}
            onKeyDown={(e) => {
              if (renaming === f.id) return
              if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault(); void removeFolders(target(f.id))
              } else if (e.key === 'Enter') { e.preventDefault(); pick({ kind: 'folder', id: f.id }) }
              else if (e.key === 'Escape' && picked.length) { setPicked([]); setAnchor(null) }
            }}
            onContextMenu={(e) => {
              // Right-clicking outside the selection moves it there, so the
              // menu always describes what is actually highlighted.
              if (picked.length && !picked.includes(f.id)) { setPicked([]); setAnchor(f.id) }
              setMenu({
              ...menuPos(e),
              items: target(f.id).length > 1 ? [
                {
                  label: `Delete ${target(f.id).length} Folders`,
                  danger: true,
                  onClick: () => void removeFolders(target(f.id)),
                },
              ] : [
                { label: 'Rename Folder', onClick: () => setRenaming(f.id) },
                { label: 'New Folder', onClick: () => setDrafting(true) },
                { type: 'separator' },
                { label: 'Delete Folder', danger: true, onClick: () => void removeFolders([f.id]) },
              ],
              })
            }}
          >
            <span className="icon"><FolderIcon /></span>
            {renaming === f.id ? (
              <input autoFocus defaultValue={f.name}
                     onBlur={(e) => { store.renameFolder(f.id, e.target.value); setRenaming(null) }}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter') e.currentTarget.blur()
                       if (e.key === 'Escape') setRenaming(null)
                     }} />
            ) : (
              <span className="name">{f.name}</span>
            )}
            <span className="count">{folderCount(store, f.id)}</span>
          </div>
        ))}

        {drafting && (
          <div className="folder-row">
            <span className="icon"><FolderIcon /></span>
            <input autoFocus value={draft} placeholder="New Folder"
                   onChange={(e) => setDraft(e.target.value)} onBlur={commitDraft}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') e.currentTarget.blur()
                     if (e.key === 'Escape') { setDraft(''); setDrafting(false) }
                   }} />
          </div>
        )}

        <button
          className={[
            'folder-row',
            sameScope(scope, { kind: 'trash' }) ? 'selected' : '',
            dropOn === 'trash' ? 'drop-on' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => pick({ kind: 'trash' })}
          {...dropTarget('trash', (ids) => store.trashMany(ids))}
        >
          <span className="icon"><TrashIcon /></span>
          <span className="name">Recently Deleted</span>
          <span className="count">{trashCount(store) || ''}</span>
        </button>

        <button className="folder-row muted" onClick={() => setDrafting(true)}>
          <span className="icon muted"><PlusIcon /></span>
          <span className="name">New Folder</span>
        </button>

        <div className="sidebar-heading">Tags</div>
        {tags.length === 0 && <div className="sidebar-empty">Type #tag in a note</div>}
        {tags.map((t) => (
          <button key={t.tag}
                  className={`folder-row ${sameScope(scope, { kind: 'tag', tag: t.tag }) ? 'selected' : ''}`}
                  onClick={() => pick({ kind: 'tag', tag: t.tag })}>
            <span className="icon"><TagIcon /></span>
            <span className="name">{t.tag}</span>
            <span className="count">{t.count}</span>
          </button>
        ))}
      </div>

      {store.user && (
        <div className="account">
          {store.user.photoURL && <img src={store.user.photoURL} alt="" referrerPolicy="no-referrer" />}
          <button className="who" title="Settings" onClick={() => setSettings(true)}>
            {store.user.email}
          </button>
          <button className="signout" title="Sign Out" onClick={() => void signOut()}>Sign Out</button>
        </div>
      )}

      {settings && <Settings onClose={() => setSettings(false)} />}

      <ContextMenu state={menu} onClose={() => setMenu(null)} />
    </aside>
  )
}
